import { Icon } from 'src/components/widgets/icon';
import Stack from 'src/components/widgets/stack/stack';
import styled from 'styled-components';

/**
 * Building blocks shared by the switch dialogs that show the master → new master overview:
 * the failure dialogs and the "switch started" step of the wizard.
 */

export const TONES = {
    success: { color: '#2e7d32', background: '#e8f5e9' },
    error: { color: '#c62828', background: '#fdecea' },
    warning: { color: '#b26a00', background: '#fff3e0' }
} as const;

export type DialogTone = keyof typeof TONES;

export const Layout = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
`;

export const Header = styled.div`
    display: flex;
    gap: 16px;
    align-items: flex-start;
    padding-right: 24px;
`;

export const HeaderIcon = styled(Icon)<{ $tone: DialogTone }>`
    width: 40px;
    height: 40px;
    min-width: 40px;
    border-radius: 50%;
    background-color: ${(props) => TONES[props.$tone].background};
    color: ${(props) => TONES[props.$tone].color};

    svg {
        width: 22px;
        height: 22px;
    }
`;

export const HeaderTitle = styled.h1`
    margin: 0;
    font-size: 18px;
    line-height: 24px;
    font-weight: 600;
    color: ${(props) => props.theme.app.textColor};
`;

export const HeaderCaption = styled.p`
    margin: 2px 0 0;
    font-size: 13px;
    line-height: 18px;
    color: #6b6b6b;
`;

export const Body = styled(Stack)`
    flex: 1;
    min-height: 0;
    padding-top: 24px;
`;

export const Overview = styled.div`
    padding: 20px 24px 16px;
    background-color: #f3f5f7;
    border-radius: 8px;
`;

export const OverviewChannels = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-around;
`;

export const ChannelNode = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    min-width: 0;
    max-width: 40%;
    text-align: center;
`;

export const ChannelAvatar = styled.div<{ $dashed?: boolean; $filled?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    margin-bottom: 6px;
    border-radius: 50%;
    border: 1px ${(props) => (props.$dashed ? 'dashed #b0bec5' : `solid ${props.$filled ? props.theme.app.primary : props.theme.app.borderColor}`)};
    background-color: ${(props) => (props.$filled ? props.theme.app.primary : '#ffffff')};
    color: ${(props) => (props.$filled ? '#ffffff' : '#78909c')};

    ${Icon}, ${Icon} svg {
        width: 22px;
        height: 22px;
    }
`;

export const ChannelName = styled.strong`
    max-width: 100%;
    font-size: 14px;
    font-weight: 600;
    color: ${(props) => props.theme.app.textColor};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

/** `$tone` colours the status with a dialog tone, `$emphasis` with the app's primary colour; plain grey otherwise. */
export const ChannelRole = styled.span<{ $tone?: DialogTone; $emphasis?: boolean }>`
    font-size: 12px;
    font-weight: ${(props) => (props.$tone || props.$emphasis ? 500 : 400)};
    color: ${(props) => (props.$tone ? TONES[props.$tone].color : props.$emphasis ? props.theme.app.primary : '#6b6b6b')};
`;

/** The dashed connector between the two channels, with a round badge in the middle. */
export const ChannelLink = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 14px;

    &::before,
    &::after {
        content: '';
        width: 32px;
        border-top: 2px dashed #b0bec5;
    }
`;

/** Without `$color` the badge takes the app's primary colour. */
export const ChannelLinkIcon = styled(Icon)<{ $color?: string }>`
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background-color: ${(props) => props.$color ?? props.theme.app.primary};
    color: #ffffff;

    svg {
        width: 14px;
        height: 14px;
    }
`;

export const SectionTitle = styled.h3`
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #6b6b6b;
`;

export const Item = styled.div`
    display: flex;
    gap: 10px;
    font-size: 13px;
    line-height: 18px;
`;

export const ITEM_ICON_COLORS = { default: undefined, success: TONES.success.color, warning: TONES.warning.color } as const;

export const ItemIcon = styled(Icon)<{ $variant: keyof typeof ITEM_ICON_COLORS }>`
    margin-top: 1px;
    color: ${(props) => ITEM_ICON_COLORS[props.$variant] ?? props.theme.app.textColor};
`;

export const ItemText = styled.div`
    display: flex;
    flex-direction: column;
    color: ${(props) => props.theme.app.textColor};

    span {
        font-size: 12px;
        line-height: 16px;
        color: #6b6b6b;
    }

    a {
        color: ${(props) => props.theme.app.secondary};
    }
`;
